import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "影视资讯", to: "/news" },
  { label: "关于我们", to: "/about" },
];

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const transparent = isHome && !scrolled && !menuOpen;

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMenuOpen(false);
    setQuery("");
  };

  const linkBase =
    "relative px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer";
  const linkColor = transparent
    ? "text-background-50/85 hover:text-background-50"
    : "text-foreground-700 hover:text-primary-600";
  const linkActive = transparent ? "text-background-50" : "text-primary-600";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center gap-4 md:gap-8">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-movie-2-line text-xl"></i>
          </span>
          <span
            className={`font-heading text-xl font-bold tracking-tight ${
              transparent ? "text-background-50" : "text-foreground-950"
            }`}
          >
            天狼影院
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`${linkBase} ${isActive(item.to) ? linkActive : linkColor}`}
            >
              {item.label}
              {isActive(item.to) ? (
                <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-5 h-0.5 rounded-full bg-accent-500"></span>
              ) : null}
            </Link>
          ))}
        </nav>

        <form onSubmit={submit} className="hidden md:flex items-center ml-auto">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              name="site-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索电影、电视剧、资讯"
              className={`w-52 xl:w-72 h-10 rounded-full pl-9 pr-4 text-sm outline-none transition-all focus:ring-2 focus:ring-accent-400/60 ${
                transparent
                  ? "bg-background-50/15 text-background-50 placeholder:text-background-50/60 border border-background-50/30"
                  : "bg-background-200 text-foreground-900 placeholder:text-foreground-400 border border-background-300"
              }`}
            />
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className={`lg:hidden ml-auto md:ml-0 w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
            transparent ? "text-background-50" : "text-foreground-800"
          }`}
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
        </button>
      </div>

      {menuOpen ? (
        <div className="lg:hidden bg-background-50 border-b border-background-200 px-4 pb-5 pt-2 animate-fade-up">
          <form onSubmit={submit} className="mb-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                name="mobile-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索电影、电视剧、资讯"
                className="w-full h-11 rounded-full pl-9 pr-4 text-sm outline-none bg-background-200 text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:ring-2 focus:ring-accent-400/60"
              />
            </div>
          </form>
          <nav className="flex flex-col">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-3 rounded-md text-sm font-medium cursor-pointer ${
                  isActive(item.to)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}